import client1 from "@/src/server/clients/tri-air-database/tri-air-database-client";
import client2 from "@/src/server/clients/tri-company-partner/tri-company-partner-client";
import carrierClient from "@/src/server/clients/tri-company/tri-company-issuing-carrier-client";
import client4 from "@/src/server/clients/tri-company/tri-company-shipper-consignee-client";
import client3 from "@/src/server/clients/tri-user-client";
import authStore from "@/src/store/authStore";
import { AirAirlineProto, AirAirportProto } from "@onetakesolutions/cargogo-contract-web/Entity_pb";
import {
  GetAirlinesResponse,
  GetAirportsResponse,
  GetCountriesResponse,
  GetCountryCodesResponse,
  GetPortOfStationsResponse,
  GetPortOfStationsResponse_Data_PortOfStation,
  GetSystemOptionsResponse,
} from "@onetakesolutions/cargogo-contract-web/TriAirDatabaseService_pb";
import { GetShipperConsigneesResponse } from "@onetakesolutions/cargogo-contract-web/TriCompanyDatabase_pb";
import {
  GetPartnersResponse,
  GetSuppliersResponse,
} from "@onetakesolutions/cargogo-contract-web/TriCompanyPartnerService_pb";
import { GetCompanyUsersResponse } from "@onetakesolutions/cargogo-contract-web/TriUserService_pb";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { CComboboxOption } from "../components/cCombobox/CCombobox";
import indexedDBStorage from "../util/indexed-db-storage";
import { formatIssuingCarrierOptionLabel } from "../util/issuing-carrier";

type FetchOptionType =
  | "airports"
  | "airlines"
  | "partnerSalesmen"
  | "partners"
  | "suppliers"
  | "issuingCarriers"
  | "freightContractShippingOptions"
  | "shipperConsignees"
  | "regulationRequestCategories"
  | "countryCodes"
  | "portOfStations";

export interface SelectOptionsState {
  timestamp?: number;
  loading: boolean;
  error: boolean;
  hydrated: boolean;
  options?: {
    airports?: AirAirportProto[];
    airlines?: AirAirlineProto[];
    countries?: string[];
    countryCodes?: string[];
    portOfStations?: GetPortOfStationsResponse_Data_PortOfStation[];
    zones?: CComboboxOption[];
    partnerRoles?: CComboboxOption[];
    partnerSalesmen?: CComboboxOption[];
    surchargeCalBy?: CComboboxOption[];
    surchargePerUnit?: CComboboxOption[];
    surchargeHaul?: CComboboxOption[];
    suppliers?: CComboboxOption[];
    partners?: CComboboxOption[];
    issuingCarriers?: CComboboxOption[];
    shipperConsignees?: CComboboxOption[];
    freightContractShippingOptions?: CComboboxOption[];
    regulationRequestCategories?: CComboboxOption[];
    [key: string]: any;
  };
  fetch: () => Promise<void>;
  /** @internal implementation of fetch(); do not call directly — use fetch(). */
  _doFetch: () => Promise<void>;
  request: (
    type:
      | "airports"
      | "airlines"
      | "partnerSalesmen"
      | "partners"
      | "suppliers"
      | "issuingCarriers"
      | "freightContractShippingOptions"
      | "shipperConsignees"
      | "regulationRequestCategories"
      | "countryCodes"
      | "portOfStations",
  ) => Promise<void>;
  reset: () => void;
  waitForHydration: () => Promise<void>; // Add helper method
}

const APIS = {
  public: [
    "getAirports",
    "getCountries",
    "getCountryCodes",
    "getPortOfStations",
    "getAirlines",
    "getSystemOptions",
  ],
  private: ["getCompanyUsers", "getPartners", "getSuppliers"],
};

const fetchPublicOptions = async () => {
  const results = await Promise.allSettled([
    client1.getAirports({
      pageable: { page: 1, size: 9999, sortProperty: "id", sortDirection: "asc" },
    }),
    client1.getCountries({}),
    client1.getCountryCodes({}),
    client1.getPortOfStations({}),
    client1.getAirlines({
      pageable: { page: 1, size: 9999, sortProperty: "id", sortDirection: "asc" },
    }),
    client1.getSystemOptions({}),
  ]);
  return results.map((result, index) => {
    if (result.status === "fulfilled") {
      return result.value;
    } else {
      console.error(`[ERR] ${APIS.public[index]} failed:`, result.reason);
      return null;
    }
  });
};

/**
 * Issuing carrier options are keyed by carrier NAME — must match
 * FormIssuingCarrierCCombobox (create/edit ground truth). Freight cost/sales
 * `issuingCarrierId` stores the carrier name despite the field name.
 * Reflects only the Issuing Carrier Management list — no auto-injected self-company option.
 */
const fetchIssuingCarriersOptions = async (): Promise<{
  success: boolean;
  options: CComboboxOption[];
}> => {
  try {
    const carriersRes = await carrierClient.getIssuingCarriers({
      q: "",
      pageable: { page: 1, size: 9999 },
    });
    const options = (carriersRes.data?.carriers ?? []).map((c) => ({
      label: formatIssuingCarrierOptionLabel(c.name, c.airline),
      value: c.name,
    }));
    return { success: true, options };
  } catch (error) {
    console.error("[ERR] getIssuingCarriers failed:", error);
    return { success: false, options: [] };
  }
};

const fetchPrivateOptions = async (): Promise<
  (
    | GetCompanyUsersResponse
    | GetPartnersResponse
    | GetSuppliersResponse
    | GetShipperConsigneesResponse
    | { success: boolean; options: CComboboxOption[] }
    | null
  )[]
> => {
  const { user } = authStore.getState();
  if (!user) {
    return [null, null, null, null, null];
  }
  const [results, issuingCarriers] = await Promise.all([
    Promise.allSettled([
      client3.getCompanyUsers({ companySeqNumber: user.companySeqNumber }),
      client2.getPartners({ pageable: { page: 1, size: 9999 } }),
      client2.getSuppliers({}),
      client4.getShipperConsignees({ pageable: { page: 1, size: 9999 } }),
    ]),
    fetchIssuingCarriersOptions(),
  ]);
  // console.log("[DEBUG] FetchPrivateOptions results:", results);
  return [
    ...results.map((result, index) => {
      if (result.status === "fulfilled") {
        return result.value;
      } else {
        console.error(`[ERR] ${APIS.private[index]} failed:`, result.reason);
        return null;
      }
    }),
    issuingCarriers,
  ];
};

const fetchOption = async (type: FetchOptionType) => {
  const { user } = authStore.getState();
  try {
    switch (type) {
      case "airports":
        return await client1.getAirports({
          pageable: { page: 1, size: 9999, sortProperty: "id", sortDirection: "asc" },
        });
      case "airlines":
        return await client1.getAirlines({
          pageable: { page: 1, size: 9999, sortProperty: "id", sortDirection: "asc" },
        });
      case "partnerSalesmen":
        if (!user) {
          throw new Error("User not authenticated");
        }
        return await client3.getCompanyUsers({ companySeqNumber: user.companySeqNumber });
      case "partners":
        if (!user) {
          throw new Error("User not authenticated");
        }
        return await client2.getPartners({ pageable: { page: 1, size: 9999 } });
      case "suppliers":
        if (!user) {
          throw new Error("User not authenticated");
        }
        return await client2.getSuppliers({});
      case "issuingCarriers": {
        const { options: merged } = await fetchIssuingCarriersOptions();
        return merged as any;
      }
      case "freightContractShippingOptions":
        return await client1.getSystemOptions({});
      case "shipperConsignees":
        if (!user) {
          throw new Error("User not authenticated");
        }
        return await client4.getShipperConsignees({ pageable: { page: 1, size: 9999 } });
      case "regulationRequestCategories":
        return await client1.getSystemOptions({});
      case "countryCodes":
        return await client1.getCountryCodes({});
      case "portOfStations":
        return await client1.getPortOfStations({});
      default:
        throw new Error(`Unknown option type: ${type}`);
    }
  } catch (error) {
    console.error(`[ERR] Failed to fetch ${type}:`, error);
    throw error;
  }
};

const parseOptionsData = (
  publicResults: (
    | GetAirportsResponse
    | GetAirlinesResponse
    | GetCountriesResponse
    | GetCountryCodesResponse
    | GetPortOfStationsResponse
    | GetSystemOptionsResponse
    | null
  )[],
  privateResults: (
    | GetCompanyUsersResponse
    | GetPartnersResponse
    | GetSuppliersResponse
    | GetShipperConsigneesResponse
    | { success: boolean; options: CComboboxOption[] }
    | null
  )[],
) => {
  // Order must mirror fetchPublicOptions() call order exactly — positional destructuring.
  const [
    airportsRes,
    countriesRes,
    countryCodesRes,
    portOfStationsRes,
    airlinesRes,
    systemOptionsRes,
  ] = publicResults as [
    GetAirportsResponse | null,
    GetCountriesResponse | null,
    GetCountryCodesResponse | null,
    GetPortOfStationsResponse | null,
    GetAirlinesResponse | null,
    GetSystemOptionsResponse | null,
  ];
  // Order mirrors fetchPrivateOptions() call order: users, partners, suppliers,
  // shipperConsignees, issuingCarriers (the last one is pre-parsed, not a raw response).
  const [salesmanRes, partnerRes, supplierRes, shipperConsigneeRes, issuingCarriersRes] =
    privateResults as [
      GetCompanyUsersResponse | null,
      GetPartnersResponse | null,
      GetSuppliersResponse | null,
      GetShipperConsigneesResponse | null,
      { success: boolean; options: CComboboxOption[] } | null,
    ];
  if (process.env.NODE_ENV === "development") {
    console.log("[DEBUG] Parsed Options Results:", systemOptionsRes);
  }
  return {
    airports:
      (airportsRes?.success &&
        airportsRes.errorCode === 0 &&
        airportsRes.data?.airports?.sort((a, b) => a.airportCode.localeCompare(b.airportCode))) ||
      [],
    airlines:
      (airlinesRes?.success &&
        airlinesRes.errorCode === 0 &&
        airlinesRes.data?.airlines?.sort((a, b) => a.IATA.localeCompare(b.IATA))) ||
      [],
    countries:
      (countriesRes?.success && countriesRes.errorCode === 0 && countriesRes.data?.countries) || [],
    countryCodes:
      (countryCodesRes?.success &&
        countryCodesRes.errorCode === 0 &&
        countryCodesRes.data?.countryCodes) ||
      [],
    portOfStations:
      (portOfStationsRes?.success &&
        portOfStationsRes.errorCode === 0 &&
        portOfStationsRes.data?.portOfStations) ||
      [],
    zones:
      systemOptionsRes?.data?.enums.zones?.options.map((x) => ({
        label: x.label,
        value: x.value.value,
      })) || [],
    partnerRoles:
      systemOptionsRes?.data?.enums.partner_roles?.options.map((x) => ({
        label: x.label,
        value: x.value.value,
      })) || [],
    partnerSalesmen:
      salesmanRes?.data?.users.map((x) => ({
        label: x.name,
        value: x.id,
      })) || [],
    surchargeCalBy:
      systemOptionsRes?.data?.enums.surcharge_calBy?.options.map((x) => ({
        label: x.label,
        value: x.value.value,
      })) || [],
    surchargePerUnit:
      systemOptionsRes?.data?.enums.surcharge_perUnit?.options.map((x) => ({
        label: x.label,
        value: x.value.value,
      })) || [],
    surchargeHaul:
      systemOptionsRes?.data?.enums.surcharge_haul?.options.map((x) => ({
        label: x.label,
        value: x.value.value,
      })) || [],
    suppliers:
      supplierRes?.data?.suppliers.map((x) => ({
        label: x.legalName || x.displayName,
        value: x.id,
      })) || [],
    partners:
      partnerRes?.data?.partners.map((x) => ({
        label: x.legalName || x.displayName,
        value: x.id,
      })) || [],
    // Keyed by carrier NAME — must match FormIssuingCarrierCCombobox. See fetchIssuingCarriersOptions().
    issuingCarriers: issuingCarriersRes?.options ?? [],
    shipperConsignees:
      shipperConsigneeRes?.data?.shipperConsignees.map((x) => ({
        label: x.name,
        value: x.id,
      })) || [],
    freightContractShippingOptions:
      systemOptionsRes?.data?.enums.freight_contract_shipping_option?.options.map((x) => ({
        label: x.label,
        value: x.value.value,
      })) || [],
    regulationRequestCategories:
      systemOptionsRes?.data?.enums.regulation_request_category?.options.map((x) => ({
        label: x.label,
        value: x.value.value,
      })) || [],
  };
};

const parseOption = (
  type:
    | "airports"
    | "airlines"
    | "portOfStation"
    | "partnerSalesmen"
    | "partners"
    | "suppliers"
    | "issuingCarriers"
    | "freightContractShippingOptions"
    | "shipperConsignees"
    | "regulationRequestCategories"
    | "countryCodes"
    | "portOfStations",
  result:
    | GetAirportsResponse
    | GetAirlinesResponse
    | GetPortOfStationsResponse
    | GetCompanyUsersResponse
    | GetPartnersResponse
    | GetSuppliersResponse
    | GetSystemOptionsResponse
    | GetShipperConsigneesResponse
    | GetSystemOptionsResponse
    | GetCountryCodesResponse
    | GetPortOfStationsResponse
    | CComboboxOption[],
) => {
  const systemRes = result as GetSystemOptionsResponse;
  switch (type) {
    case "airports":
      const airportsRes = result as GetAirportsResponse;
      return (
        airportsRes.data?.airports?.sort((a, b) => a.airportCode.localeCompare(b.airportCode)) || []
      );
    case "airlines":
      const airlinesRes = result as GetAirlinesResponse;
      return airlinesRes.data?.airlines?.sort((a, b) => a.IATA.localeCompare(b.IATA)) || [];
    case "countryCodes":
      const countryCodesRes = result as GetCountryCodesResponse;
      return countryCodesRes.data?.countryCodes || [];
    case "portOfStations":
      const portOfStationsRes = result as GetPortOfStationsResponse;
      return portOfStationsRes.data?.portOfStations || [];
    // case "portOfStation":
    //   const portOfStationsRes = result as GetPortOfStationsResponse;
    //   return portOfStationsRes.data?.portOfStations || [];
    case "partnerSalesmen":
      const salesmenRes = result as GetCompanyUsersResponse;
      return (
        salesmenRes.data?.users.map((x) => ({
          label: x.name,
          value: x.id,
        })) || []
      );
    case "partners":
      const partnerRes = result as GetPartnersResponse;
      return (
        partnerRes.data?.partners.map((x) => ({
          label: x.legalName || x.displayName,
          value: x.id,
        })) || []
      );
    case "suppliers":
      const supplierRes = result as GetSuppliersResponse;
      return (
        supplierRes.data?.suppliers.map((x) => ({
          label: x.legalName || x.displayName,
          value: x.id,
        })) || []
      );
    case "issuingCarriers":
      // Already returned as CComboboxOption[] directly from fetchOption
      return Array.isArray(result) ? result : [];
    case "freightContractShippingOptions":
      return (
        systemRes.data?.enums.freight_contract_shipping_option?.options.map((x) => ({
          label: x.label,
          value: x.value.value,
        })) || []
      );
    case "shipperConsignees":
      const shipperConsigneeRes = result as GetShipperConsigneesResponse;
      return (
        shipperConsigneeRes.data?.shipperConsignees.map((x) => ({
          label: x.name,
          value: x.id,
        })) || []
      );
    case "regulationRequestCategories":
      return (
        systemRes.data?.enums.regulation_request_category?.options.map((x) => ({
          label: x.label,
          value: x.value.value,
        })) || []
      );
    default:
      return [];
  }
};

// Per-type dedupe for request() — must NOT share a single global gate with other
// types, or concurrently-mounted comboboxes drop each other's fetch silently
// (whichever type loses the race to set `loading` first never retries).
const requestInFlight = new Map<FetchOptionType, Promise<void>>();
// Separate dedupe for the bulk fetch() (login/reset) so it is never starved by an
// unrelated per-type request() sharing the old single `loading` gate.
let fetchInFlight: Promise<void> | null = null;

function isOptionsReady(options?: SelectOptionsState["options"]) {
  if (!options) return false;
  const requiredFields = ["airports", "airlines", "zones", "partnerRoles"] as const;
  return requiredFields.some((field) => {
    const value = options[field];
    return Array.isArray(value) && value.length > 0;
  });
}

export const store = create<SelectOptionsState>()(
  persist(
    (set, get) => ({
      loading: false,
      error: false,
      hydrated: false, // Initially false
      options: undefined,
      fetch: async () => {
        console.log("[DEBUG] SelectOptionsStore Fetch");
        if (fetchInFlight) {
          return fetchInFlight;
        }
        const run = get()._doFetch();
        fetchInFlight = run.finally(() => {
          fetchInFlight = null;
        });
        return fetchInFlight;
      },
      _doFetch: async () => {
        const { options: prev } = get();
        try {
          set({ loading: true, error: false });
          const [publicResults, privateResults] = await Promise.all([
            fetchPublicOptions(),
            fetchPrivateOptions(),
          ]);
          const next = parseOptionsData(publicResults, privateResults);
          const [airportsRes, countriesRes, countryCodesRes, portOfStationsRes, airlinesRes, systemOptionsRes] =
            publicResults;
          const [salesmanRes, partnerRes, supplierRes, shipperRes, issuingCarriersRes] =
            privateResults;
          /** Any single call failing (gateway/auth blip) must not wipe good cache for that field. */
          const keep = <T extends keyof typeof next>(
            key: T,
            res: { success?: boolean } | null,
          ) => (res?.success ? next[key] : (prev?.[key] ?? next[key]));
          set({
            options: {
              ...next,
              airports: keep("airports", airportsRes),
              airlines: keep("airlines", airlinesRes),
              countries: keep("countries", countriesRes),
              countryCodes: keep("countryCodes", countryCodesRes),
              portOfStations: keep("portOfStations", portOfStationsRes),
              zones: keep("zones", systemOptionsRes),
              partnerRoles: keep("partnerRoles", systemOptionsRes),
              surchargeCalBy: keep("surchargeCalBy", systemOptionsRes),
              surchargePerUnit: keep("surchargePerUnit", systemOptionsRes),
              surchargeHaul: keep("surchargeHaul", systemOptionsRes),
              freightContractShippingOptions: keep(
                "freightContractShippingOptions",
                systemOptionsRes,
              ),
              regulationRequestCategories: keep(
                "regulationRequestCategories",
                systemOptionsRes,
              ),
              // Private calls often fail during gateway/auth blips — do not persist [] over good cache.
              suppliers: keep("suppliers", supplierRes),
              partners: keep("partners", partnerRes),
              issuingCarriers: keep("issuingCarriers", issuingCarriersRes),
              shipperConsignees: keep("shipperConsignees", shipperRes),
              partnerSalesmen: keep("partnerSalesmen", salesmanRes),
            },
            timestamp: Date.now(),
            loading: false,
            error: false,
          });
        } catch (err) {
          console.error("[ERR] SelectOptionsStore fetch error:", err);
          set({ loading: false, error: true });
        }
      },
      request: async (type) => {
        console.log(`[DEBUG] SelectOptionsStore Request: ${type}`);
        await get().waitForHydration();
        const optionKey = type;
        const currentOption = get().options?.[optionKey];
        if (currentOption && Array.isArray(currentOption) && currentOption.length > 0) {
          console.log(`[DEBUG] ${type} already exists, skipping fetch`);
          return;
        }
        // Dedupe concurrent callers for the SAME type only — different types must
        // run independently so one slow/failed catalog cannot starve the others.
        const existing = requestInFlight.get(type);
        if (existing) {
          return existing;
        }
        const run = (async () => {
          set({ loading: true, error: false });
          try {
            const result = await fetchOption(type);
            const parsed = parseOption(type, result);
            // Read state at commit time — a concurrent request() for another type
            // may have updated `options` while this one was in flight.
            set((state) => ({
              error: false,
              options: {
                ...state.options,
                [optionKey]: parsed,
              },
            }));
          } catch (err) {
            console.error(`[ERR] SelectOptionsStore request ${type} error:`, err);
            set({ error: true });
          } finally {
            requestInFlight.delete(type);
            set({ loading: requestInFlight.size > 0 });
          }
        })();
        requestInFlight.set(type, run);
        return run;
      },
      waitForHydration: () => {
        return new Promise<void>((resolve) => {
          const checkHydration = () => {
            if (get().hydrated) {
              resolve();
            } else {
              setTimeout(checkHydration, 50);
            }
          };
          checkHydration();
        });
      },
      reset: () => {
        // Clear cache on account switch. Refetch after login is safe once shared GETs
        // are login-only and module APIs accept module permissions (not Admin-only).
        void store.persist.clearStorage();
        set({
          options: undefined,
          timestamp: undefined,
          loading: false,
          error: false,
        });
        void get().fetch();
      },
    }),
    {
      name: "options-storage",
      version: 2,
      migrate: (persistedState, version) => {
        const state = persistedState as Partial<SelectOptionsState> | undefined;
        if (version < 2 && state?.options) {
          return {
            ...state,
            options: { ...state.options, issuingCarriers: [] },
          };
        }
        return state as SelectOptionsState;
      },
      storage: createJSONStorage(() => indexedDBStorage),
      partialize: (state) => ({ timestamp: state.timestamp, options: state.options }),
      onRehydrateStorage: () => (state) => {
        if (process.env.NODE_ENV === "development") {
          console.log("[DEBUG] Zustand store hydrated");
        }
        store.setState({ hydrated: true });
        const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;
        const now = Date.now();
        const expired = !state?.timestamp || now - state.timestamp > THIRTY_DAYS;
        if (expired || !isOptionsReady(state?.options)) {
          if (process.env.NODE_ENV === "development") {
            console.log("[DEBUG] Data expired or incomplete — clearing and refetching");
          }
          store.persist.clearStorage();
          store.getState().fetch();
          return;
        }
        // Public cache can survive a 502 while private catalogs were persisted as [].
        // Refetch those without wiping airports/airlines.
        if (!state?.options?.suppliers?.length) {
          void store.getState().request("suppliers");
        }
        if (!state?.options?.partners?.length) {
          void store.getState().request("partners");
        }
        if (!state?.options?.issuingCarriers?.length) {
          void store.getState().request("issuingCarriers");
        }
      },
    },
  ),
);

export default store;
